import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import { Button } from 'devextreme-react/button';
import DataGrid, {
  Column,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Lookup,
  Pager,
  Paging,
  StateStoring,
} from 'devextreme-react/data-grid';

import moment from 'moment';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { useAuth } from 'context/auth';
import $createStore from 'utils/create-store-instance';

import { useGetFactoryFullNamesQuery } from 'bus/factory/services/services';
import { RequestWorkOutCheckTime } from 'bus/request/types';

import { ConfirmModal, ReportModal } from './components';

const CashList: FC = () => {
  const { getClaims }: any = useAuth();

  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [modalReportIsVisible, setModalReportIsVisible] = useState(false);
  const [requestWorkId, setRequestWorkId] = useState<number | null>(null);
  const [requestWork, setRequestWork] = useState<RequestWorkOutCheckTime | undefined>();

  const requestDateSource = useMemo(() => {
    const defaultSort = [{ selector: 'completedDate', desc: true }];
    return $createStore('/v1/RequestWork/OutCheckTime', 'id', { defaultSort });
  }, []);

  const refGrid = useRef<DataGrid | null>(null);

  const refresh = () => refGrid.current?.instance.refresh();

  const handleConfirm = (id: number, data?: RequestWorkOutCheckTime) => {
    setRequestWork(data);
    setRequestWorkId(id);
    setModalIsVisible(true);
  };

  const handleChangeVisible = useCallback((visible: boolean) => {
    if (!visible) setRequestWork(undefined);
    setModalIsVisible(visible);
  }, []);

  const handleOpenReportModal = () => setModalReportIsVisible(true);

  const { data } = useGetFactoryFullNamesQuery();

  const isCanEditCheckOutTimeSection = getClaims(['CanEditCheckOutTimeSection']);

  return (
    <div className="container-dev">
      <AppLayoutTop title="Подтверждение отгрузок">
        <ClaimsProvider claimsBlock={['CanGenerateRequestWorkMixerReport']}>
          <div className="d-flex align-center">
            <Button className="mr-1" icon="refresh" onClick={refresh} />
            <Button width={120} text="Отчет" type="default" onClick={handleOpenReportModal} />
          </div>
        </ClaimsProvider>
      </AppLayoutTop>

      {requestWorkId && (
        <ConfirmModal
          changeVisible={handleChangeVisible}
          visible={modalIsVisible}
          requestWorkId={requestWorkId}
          onRefresh={refresh}
          requestWork={requestWork}
        />
      )}

      {modalReportIsVisible && <ReportModal visible={modalReportIsVisible} changeVisible={setModalReportIsVisible} />}

      <AppCard>
        <DataGrid
          dataSource={requestDateSource as any}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          remoteOperations={true}
          onRowPrepared={(e) => {
            if (e.rowType === 'data' && e.data.isColoredRow) {
              //@ts-ignore
              e.rowElement.style.backgroundColor = '#ffc5c5';
            }
          }}
        >
          <FilterRow visible={true} />
          <StateStoring enabled={true} type="localStorage" storageKey="requestWorkOutCheckTime3" />
          <FilterPanel visible={true} />
          <HeaderFilter visible />

          <Column dataField="transport" caption="Транспорт" />
          <Column dataField="driverName" caption="Водитель" />
          <Column dataField="factoryId" caption="Площадка">
            <Lookup dataSource={data} valueExpr="id" displayExpr="name" />
          </Column>
          <Column dataField="number" caption="Номер заявки" width={150} />
          <Column dataField="quantity" caption="Объем" width={120} />
          <Column dataField="weightBefore" caption="Вес пустой машины" width={120} />
          <Column dataField="weightAfter" caption="Вес загруженной машины" width={120} />
          <Column
            dataField="completedDate"
            caption="Дата отгрузки"
            dataType="date"
            cellRender={({ data }) => (
              <span>{data.completedDate && moment(data.completedDate).format('DD.MM.YYYY HH:mm')}</span>
            )}
          />
          <Column
            dataField="checkOutTime"
            caption="Дата подтверждения"
            dataType="datetime"
            cellRender={({ data }) => (
              <span>{data.checkOutTime && moment(data.checkOutTime).format('DD.MM.YYYY HH:mm')}</span>
            )}
          />

          <Column
            dataField="actions"
            caption=""
            width={190}
            allowFiltering={false}
            allowSorting={false}
            allowSearch={false}
            visible={isCanEditCheckOutTimeSection}
            cellRender={(data) => (
              <>
                {!data.data.isCheckOut && (
                  <ClaimsProvider claimsBlock={['CanEditCheckOutTimeSection']}>
                    <Button
                      type="default"
                      stylingMode="text"
                      text="Подтвердить"
                      onClick={() => handleConfirm(data.data.id)}
                    />
                  </ClaimsProvider>
                )}
                {data.data.isCheckOut && (
                  <Button
                    icon="edit"
                    type="default"
                    stylingMode="text"
                    onClick={() => handleConfirm(data.data.id, data.data)}
                  />
                )}
              </>
            )}
          />
          <Paging defaultPageSize={15} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default CashList;
